import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Camera, ArrowLeft, Package, Trash2, Check, X, CheckSquare, Square } from "lucide-react";
import BarcodeScanner from "react-qr-barcode-scanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_asset-tracker-117/artifacts/sfo88lq2_%E6%B5%8E%E5%8D%97%E6%B2%83%E5%BE%B7LOGO-JPG%E7%89%88.jpg";

const BulkScanPage = ({ user }) => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(true);
  const [scannedItems, setScannedItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [manualAssetId, setManualAssetId] = useState("");
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [actionType, setActionType] = useState("");
  const [borrowerName, setBorrowerName] = useState("");
  const [supervisorName, setSupervisorName] = useState(user?.full_name || "");
  const [targetReturnDate, setTargetReturnDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [barcodeInput, setBarcodeInput] = useState("");
  
  // Use refs for barcode scanning state to avoid stale closures
  const lastScanTimeRef = useRef(0);
  const barcodeBufferRef = useRef("");
  const barcodeTimeoutRef = useRef(null);

  // Memoize addScannedItem to prevent stale closures
  const addScannedItem = useCallback(async (assetId) => {
    // Check if this barcode has already been scanned
    setScannedItems(prevItems => {
      const existingItem = prevItems.find(item => item.asset_id === assetId);
      if (existingItem) {
        toast.error(`资产 ${assetId} 已在列表中，请勿重复扫描！`, {
          duration: 3000,
        });
        
        // Highlight the existing item temporarily
        const element = document.querySelector(`[data-asset-id="${assetId}"]`);
        if (element) {
          element.style.backgroundColor = '#fef3c7';
          element.style.border = '2px solid #f59e0b';
          setTimeout(() => {
            element.style.backgroundColor = '';
            element.style.border = '';
          }, 2000);
        }
        return prevItems;
      }
      return prevItems;
    });

    // Fetch asset info
    try {
      const response = await axios.post(`${API}/assets/bulk-scan`, [assetId]);
      if (response.data.length > 0) {
        const newItem = { ...response.data[0], action: "", selected: false };
        setScannedItems(prev => {
          // Double check to avoid duplicates
          if (prev.find(item => item.asset_id === assetId)) {
            return prev;
          }
          return [...prev, newItem];
        });
        toast.success(`已添加: ${assetId}`);
      } else {
        toast.error("未找到资产");
      }
    } catch (error) {
      toast.error("获取资产信息失败");
    }
  }, []);

  // Enhanced barcode scanner support with improved initialization
  useEffect(() => {
    let isInitialized = false;
    
    // Small delay to ensure component is fully mounted
    const initTimeout = setTimeout(() => {
      isInitialized = true;
      console.log('Barcode scanner initialized');
    }, 500);
    
    const handleKeyPress = (e) => {
      if (!isInitialized) {
        console.log('Scanner not initialized yet, ignoring keypress');
        return;
      }
      
      const currentTime = Date.now();
      
      // If more than 100ms between keystrokes, reset buffer (new scan)
      if (currentTime - lastScanTimeRef.current > 100) {
        barcodeBufferRef.current = "";
      }
      
      lastScanTimeRef.current = currentTime;
      
      // Handle Enter key (end of barcode scan)
      if (e.key === 'Enter') {
        e.preventDefault();
        const scannedCode = barcodeBufferRef.current.trim();
        console.log('Barcode scan completed:', scannedCode, 'Buffer length:', scannedCode.length);
        
        if (scannedCode.length > 0) {
          addScannedItem(scannedCode);
          barcodeBufferRef.current = "";
        }
        return;
      }
      
      // Ignore special keys
      if (e.key.length > 1 && e.key !== 'Enter') {
        return;
      }
      
      // Add character to buffer
      barcodeBufferRef.current += e.key;
      console.log('Buffer updated:', barcodeBufferRef.current);
      
      // Clear timeout and set new one
      if (barcodeTimeoutRef.current) {
        clearTimeout(barcodeTimeoutRef.current);
      }
      
      // If buffer gets too long without Enter, reset it
      barcodeTimeoutRef.current = setTimeout(() => {
        console.log('Buffer timeout, resetting:', barcodeBufferRef.current);
        barcodeBufferRef.current = "";
      }, 1000);
    };
    
    // Add event listener
    document.addEventListener('keypress', handleKeyPress);
    
    // Cleanup
    return () => {
      clearTimeout(initTimeout);
      document.removeEventListener('keypress', handleKeyPress);
      if (barcodeTimeoutRef.current) {
        clearTimeout(barcodeTimeoutRef.current);
      }
    };
  }, [addScannedItem]);

  const handleScan = async (err, result) => {
    if (result && result.text) {
      await addScannedItem(result.text);
    }
  };

  const handleManualAdd = () => {
    if (manualAssetId.trim()) {
      addScannedItem(manualAssetId.trim());
      setManualAssetId("");
    }
  };

  const removeItem = (assetId) => {
    setScannedItems(prev => prev.filter(item => item.asset_id !== assetId));
    setSelectedItems(prev => {
      const newSelected = new Set(prev);
      newSelected.delete(assetId);
      return newSelected;
    });
  };

  const toggleItemSelection = (assetId) => {
    setSelectedItems(prev => {
      const newSelected = new Set(prev);
      if (newSelected.has(assetId)) {
        newSelected.delete(assetId);
      } else {
        newSelected.add(assetId);
      }
      return newSelected;
    });
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(scannedItems.map(item => item.asset_id)));
    }
    setSelectAll(!selectAll);
  };

  // Update selectAll state when selected items change
  useEffect(() => {
    const allSelected = scannedItems.length > 0 && selectedItems.size === scannedItems.length;
    setSelectAll(allSelected);
  }, [selectedItems, scannedItems]);

  const setBulkAction = (action) => {
    const selectedAssetIds = Array.from(selectedItems);
    if (selectedAssetIds.length === 0) {
      toast.error("请先选择资产");
      return;
    }

    // Set the action type and open the action dialog
    setActionType(action);
    setShowActionDialog(true);
    
    toast.success(`已选择操作: ${
      action === 'borrow' ? '借用' :
      action === 'return' ? '归还' :
      action === 'maintenance' ? '维修' :
      action === 'maintenance_complete' ? '确认维修' :
      action === 'confirm' ? '确认状态' : action
    }`);
  };

  const executeActions = async () => {
    // First, set the action for all selected items
    const selectedAssetIds = Array.from(selectedItems);
    const itemsWithAction = scannedItems
      .filter(item => selectedItems.has(item.asset_id))
      .map(item => ({ ...item, action: actionType }));
    
    if (itemsWithAction.length === 0) {
      toast.error("请先选择资产");
      return;
    }

    if (actionType === "borrow" && (!borrowerName || !targetReturnDate)) {
      toast.error("借用操作需要借用人和归还日期");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/assets/bulk-action`, {
        items: itemsWithAction.map(item => ({
          asset_id: item.asset_id,
          action: item.action
        })),
        borrower_name: borrowerName,
        supervisor_name: supervisorName,
        target_return_date: targetReturnDate
      });

      if (response.data.success.length > 0) {
        toast.success(`成功处理 ${response.data.success.length} 个资产`);
        
        // Update the scanned items to show the completed actions
        setScannedItems(prev => 
          prev.map(item => 
            selectedItems.has(item.asset_id) ? { ...item, action: actionType } : item
          )
        );
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach(error => toast.error(error));
      }

      // Clear form and close dialog
      setBorrowerName("");
      setTargetReturnDate("");
      setShowActionDialog(false);
      setActionType("");
      setSelectedItems(new Set());
    } catch (error) {
      console.error("Bulk action error:", error);
      toast.error("批量操作失败");
    } finally {
      setLoading(false);
    }
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white px-6 py-4">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate("/")} className="text-white hover:bg-slate-800">
            <ArrowLeft className="w-4 h-4 mr-2" />
            返回首页
          </Button>
          <div className="flex items-center space-x-4">
            <img 
              src={LOGO_URL} 
              alt="Logo" 
              className="h-8 w-8 object-contain rounded bg-white p-1"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <h1 className="text-lg font-semibold">批量扫描</h1>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Scanner Section */}
          <Card>
            <CardHeader>
              <CardTitle>扫描资产</CardTitle>
              <CardDescription>
                扫描或手动输入资产编号 (重复扫描会自动去重)
                <br />
                <span className="text-green-600 text-xs">
                  ✅ 支持物理扫码枪：直接扫描即可自动识别
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {scanning ? (
                <div className="aspect-square rounded-lg overflow-hidden">
                  <BarcodeScanner width="100%" height="100%" onUpdate={handleScan} />
                </div>
              ) : (
                <div className="aspect-square bg-slate-100 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <Camera className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                    <p className="text-slate-500">扫描器已停止</p>
                  </div>
                </div>
              )}
              
              <Button onClick={() => setScanning(!scanning)} variant="outline" className="w-full">
                {scanning ? "停止扫描" : "开始扫描"}
              </Button>
              
              <div className="flex space-x-2">
                <Input
                  placeholder="手动输入资产编号"
                  value={manualAssetId}
                  onChange={(e) => setManualAssetId(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleManualAdd()}
                />
                <Button onClick={handleManualAdd}>添加</Button>
              </div>

              {/* Barcode Scanner Status */}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm text-blue-800">
                  <strong>扫码枪模式已启用</strong>
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  • 摄像头扫描：点击上方扫描区域
                  <br />
                  • 物理扫码枪：直接扫描条形码即可自动添加
                  <br />
                  • 手动输入：使用上方输入框
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scanned Items List */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>已扫描资产 ({scannedItems.length})</CardTitle>
                  <CardDescription>
                    选择资产并设置操作 | 已选择: {selectedItems.size}
                  </CardDescription>
                </div>
                <div className="flex space-x-2">
                  {scannedItems.length > 0 && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={toggleSelectAll}
                      >
                        {selectAll ? (
                          <><CheckSquare className="w-4 h-4 mr-1" /> 取消全选</>
                        ) : (
                          <><Square className="w-4 h-4 mr-1" /> 全选</>
                        )}
                      </Button>
                      
                      {selectedItems.size > 0 && (
                        <div className="flex items-center gap-2">
                          <Select onValueChange={setBulkAction}>
                            <SelectTrigger className="w-48">
                              <SelectValue placeholder="选择批量操作" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="borrow">批量借用</SelectItem>
                              <SelectItem value="return">批量归还</SelectItem>
                              <SelectItem value="maintenance">批量维修</SelectItem>
                              <SelectItem value="maintenance_complete">确认维修</SelectItem>
                              <SelectItem value="confirm">批量确认状态</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
                        <DialogTrigger asChild>
                          <Button className="bg-green-600 hover:bg-green-700">执行操作</Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>
                              {actionType === 'borrow' ? '批量借用确认' :
                               actionType === 'return' ? '批量归还确认' :
                               actionType === 'maintenance' ? '批量维修确认' :
                               actionType === 'maintenance_complete' ? '确认维修完成' :
                               actionType === 'confirm' ? '批量状态确认' : '批量操作确认'}
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            {actionType === "borrow" && (
                              <>
                                <div>
                                  <Label>借用人姓名 *</Label>
                                  <Input value={borrowerName} onChange={(e) => setBorrowerName(e.target.value)} />
                                </div>
                                <div>
                                  <Label>预计归还日期 *</Label>
                                  <Input 
                                    type="date" 
                                    value={targetReturnDate} 
                                    onChange={(e) => setTargetReturnDate(e.target.value)}
                                    min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                                  />
                                </div>
                              </>
                            )}
                            <div>
                              <Label>主管姓名</Label>
                              <Input value={supervisorName} onChange={(e) => setSupervisorName(e.target.value)} />
                            </div>
                            <div className="text-sm text-slate-600">
                              将对 {selectedItems.size} 个资产执行{
                                actionType === 'borrow' ? '借用' :
                                actionType === 'return' ? '归还' :
                                actionType === 'maintenance' ? '维修' :
                                actionType === 'maintenance_complete' ? '确认维修' :
                                actionType === 'confirm' ? '状态确认' : '操作'
                              }
                            </div>
                            <Button onClick={executeActions} disabled={loading} className="w-full">
                              {loading ? "处理中..." : "确认执行"}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {scannedItems.map((item) => (
                  <div 
                    key={item.asset_id} 
                    data-asset-id={item.asset_id}
                    className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50 transition-all"
                  >
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleItemSelection(item.asset_id)}
                      className="flex-shrink-0"
                    >
                      {selectedItems.has(item.asset_id) ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-400" />
                      )}
                    </button>

                    <div className="flex-1">
                      <div className="font-medium">{item.asset_name}</div>
                      <div className="text-sm text-slate-500">{item.asset_id}</div>
                      <Badge variant={item.current_status === "available" ? "default" : 
                                   item.current_status === "pending_confirmation" ? "secondary" : "destructive"}>
                        {item.current_status === "available" || item.current_status === "可使用" ? "可用" : 
                         item.current_status === "rented_out" || item.current_status === "已借出" ? "已借出" : 
                         item.current_status === "under_maintenance" || item.current_status === "维修中" ? "维修中" : 
                         item.current_status === "pending_confirmation" || item.current_status === "状态待确认" ? "待确认" : item.current_status}
                      </Badge>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Button variant="ghost" size="sm" onClick={() => removeItem(item.asset_id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {scannedItems.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    暂无扫描的资产
                  </div>
                )}
              </div>

              {selectedItems.size > 0 && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="text-sm text-blue-800">
                    <strong>已选择 {selectedItems.size} 个资产</strong>
                  </div>
                  <div className="text-xs text-blue-600 mt-1">
                    使用上方的批量操作按钮为选中的资产设置相同操作
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BulkScanPage;